export const usageOverview = {
  period: '2025-03',
  totalMeetings: 184200,
  totalAiTokens: 9200000000,
  totalStorageGb: 12400,
  tenantsOverMeetingQuota: 6,
  tenantsOverTokenQuota: 3,
  projectedOverageUsd: 12800,
};

/** Stacked bar: per plan used vs limit for each metric key */
export const usageByPlanStack = {
  meetings: [
    { plan: 'Starter', used: 42000, limit: 50000 },
    { plan: 'Growth', used: 118000, limit: 120000 },
    { plan: 'Enterprise', used: 890000, limit: 1200000 },
  ],
  ai_tokens: [
    { plan: 'Starter', used: 180, limit: 250 },
    { plan: 'Growth', used: 820, limit: 900 },
    { plan: 'Enterprise', used: 5400, limit: 6000 },
  ],
  storage: [
    { plan: 'Starter', used: 420, limit: 625 },
    { plan: 'Growth', used: 3100, limit: 4000 },
    { plan: 'Enterprise', used: 8200, limit: 10000 },
  ],
  channels: [
    { plan: 'Starter', used: 12000, limit: 20000 },
    { plan: 'Growth', used: 45000, limit: 50000 },
    { plan: 'Enterprise', used: 180000, limit: 200000 },
  ],
  users: [
    { plan: 'Starter', used: 820, limit: 1000 },
    { plan: 'Growth', used: 2100, limit: 2500 },
    { plan: 'Enterprise', used: 5200, limit: 6000 },
  ],
};

export const usageTopTenantsByMetric = {
  meetings: Array.from({ length: 10 }, (_, i) => ({
    tenantCode: `MH-${String(i + 1).padStart(4, '0')}`,
    companyName: `Top Usage ${i + 1}`,
    value: 12000 - i * 900,
  })),
  ai_tokens: Array.from({ length: 10 }, (_, i) => ({
    tenantCode: `MH-${String(i + 3).padStart(4, '0')}`,
    companyName: `AI Heavy ${i + 1}`,
    value: 420 - i * 28,
  })),
  storage: Array.from({ length: 10 }, (_, i) => ({
    tenantCode: `MH-${String(i + 5).padStart(4, '0')}`,
    companyName: `Storage Co ${i + 1}`,
    value: 890 - i * 55,
  })),
  channels: Array.from({ length: 10 }, (_, i) => ({
    tenantCode: `MH-${String(i + 2).padStart(4, '0')}`,
    companyName: `Channel Co ${i + 1}`,
    value: 24000 - i * 1800,
  })),
  users: Array.from({ length: 10 }, (_, i) => ({
    tenantCode: `MH-${String(i + 4).padStart(4, '0')}`,
    companyName: `Seats Co ${i + 1}`,
    value: 1800 - i * 120,
  })),
};

/** 6 month trend points per metric */
export const usageTrend6m = {
  meetings: [
    { month: '2024-10', value: 152000 },
    { month: '2024-11', value: 158400 },
    { month: '2024-12', value: 161200 },
    { month: '2025-01', value: 170100 },
    { month: '2025-02', value: 178900 },
    { month: '2025-03', value: 184200 },
  ],
  ai_tokens: [
    { month: '2024-10', value: 7.2 },
    { month: '2024-11', value: 7.8 },
    { month: '2024-12', value: 8.1 },
    { month: '2025-01', value: 8.6 },
    { month: '2025-02', value: 8.9 },
    { month: '2025-03', value: 9.2 },
  ],
  storage: [
    { month: '2024-10', value: 10200 },
    { month: '2024-11', value: 10800 },
    { month: '2024-12', value: 11200 },
    { month: '2025-01', value: 11600 },
    { month: '2025-02', value: 12000 },
    { month: '2025-03', value: 12400 },
  ],
  channels: [
    { month: '2024-10', value: 210000 },
    { month: '2024-11', value: 225000 },
    { month: '2024-12', value: 238000 },
    { month: '2025-01', value: 251000 },
    { month: '2025-02', value: 264000 },
    { month: '2025-03', value: 278000 },
  ],
  users: [
    { month: '2024-10', value: 4200 },
    { month: '2024-11', value: 4350 },
    { month: '2024-12', value: 4480 },
    { month: '2025-01', value: 4620 },
    { month: '2025-02', value: 4780 },
    { month: '2025-03', value: 4920 },
  ],
};

export const tenantUsageRows = Array.from({ length: 20 }, (_, i) => ({
  tenantId: `tnt-${5000 + i}`,
  tenantCode: `MH-${String(6000 + i).padStart(4, '0')}`,
  companyName: `Usage Tenant ${i + 1}`,
  meetingsUsed: 800 + i * 120,
  meetingsQuota: 1000 + (i % 3) * 500,
  aiTokensUsedM: 2.4 + i * 0.15,
  aiTokensQuotaM: 3,
  storageUsedGb: 40 + i * 8,
  storageQuotaGb: 200,
  channelsUsed: 12000 + i * 500,
  channelsQuota: 20000,
  usersUsed: 40 + i * 3,
  usersQuota: 50 + (i % 3) * 20,
  overageRisk: ['low', 'medium', 'high'][i % 3],
}));

export const quotaPolicies = [
  { id: 'qp-1', name: 'Default meeting cap', resource: 'meetings', enforcement: 'hard_block', scope: 'all_tenants' },
  { id: 'qp-2', name: 'AI token soft cap', resource: 'ai_tokens', enforcement: 'throttle', scope: 'growth_plans' },
  { id: 'qp-3', name: 'Storage hard cap', resource: 'storage_gb', enforcement: 'hard_block', scope: 'starter_plans' },
];

/** Detailed policy config per plan template */
export const quotaPolicyDetailByPlan = {
  Starter: {
    meetings: {
      warningThresholdPct: 80,
      hardStop: true,
      channels: { email: true, teams: false, whatsapp: false, inApp: true },
      graceDays: 3,
      blockBehavior: 'throttle',
    },
    ai_tokens: {
      warningThresholdPct: 75,
      hardStop: false,
      channels: { email: true, teams: true, whatsapp: false, inApp: true },
      graceDays: 5,
      blockBehavior: 'notify',
    },
    storage_gb: {
      warningThresholdPct: 85,
      hardStop: true,
      channels: { email: true, teams: false, whatsapp: false, inApp: true },
      graceDays: 2,
      blockBehavior: 'block_new',
    },
    users: {
      warningThresholdPct: 90,
      hardStop: true,
      channels: { email: true, teams: true, whatsapp: false, inApp: true },
      graceDays: 7,
      blockBehavior: 'block_new',
    },
  },
  Growth: {
    meetings: {
      warningThresholdPct: 80,
      hardStop: true,
      channels: { email: true, teams: true, whatsapp: true, inApp: true },
      graceDays: 5,
      blockBehavior: 'throttle',
    },
    ai_tokens: {
      warningThresholdPct: 80,
      hardStop: true,
      channels: { email: true, teams: true, whatsapp: false, inApp: true },
      graceDays: 3,
      blockBehavior: 'throttle',
    },
    storage_gb: {
      warningThresholdPct: 80,
      hardStop: true,
      channels: { email: true, teams: true, whatsapp: false, inApp: true },
      graceDays: 4,
      blockBehavior: 'throttle',
    },
    users: {
      warningThresholdPct: 85,
      hardStop: true,
      channels: { email: true, teams: true, whatsapp: true, inApp: true },
      graceDays: 7,
      blockBehavior: 'block_new',
    },
  },
  Enterprise: {
    meetings: {
      warningThresholdPct: 90,
      hardStop: false,
      channels: { email: true, teams: true, whatsapp: true, inApp: true },
      graceDays: 14,
      blockBehavior: 'notify',
    },
    ai_tokens: {
      warningThresholdPct: 85,
      hardStop: false,
      channels: { email: true, teams: true, whatsapp: true, inApp: true },
      graceDays: 10,
      blockBehavior: 'notify',
    },
    storage_gb: {
      warningThresholdPct: 90,
      hardStop: false,
      channels: { email: true, teams: true, whatsapp: true, inApp: true },
      graceDays: 14,
      blockBehavior: 'notify',
    },
    users: {
      warningThresholdPct: 92,
      hardStop: false,
      channels: { email: true, teams: true, whatsapp: true, inApp: true },
      graceDays: 14,
      blockBehavior: 'notify',
    },
  },
};

export const overageReviewItems = Array.from({ length: 8 }, (_, i) => ({
  id: `ov-${i}`,
  tenantCode: `MH-${String(7000 + i).padStart(4, '0')}`,
  companyName: `Overage Review ${i + 1}`,
  resource: ['ai_tokens', 'meetings', 'storage_gb'][i % 3],
  overagePct: 12 + i * 7,
  overAmount: 120 + i * 40,
  since: new Date(Date.now() - (i + 1) * 86400000 * 3).toISOString(),
  amountUsd: 150 + i * 90,
  status: i % 2 === 0 ? 'pending_approval' : 'waived',
  owner: `finops${i % 3}@mh.ai`,
}));

export const overageHistoryItems = Array.from({ length: 5 }, (_, i) => ({
  id: `ovh-${i}`,
  tenantCode: `MH-${String(7200 + i).padStart(4, '0')}`,
  companyName: `Resolved ${i + 1}`,
  resource: 'meetings',
  resolvedAt: new Date(Date.now() - (i + 10) * 86400000).toISOString(),
  action: 'Temporary increase approved',
}));
